import React from 'react';
import { authService } from '../services/authService';
import { adminUserService } from '../services/adminUserService';
import { settingsService } from '../services/settingsService';
import Modal from '../components/ui/Modal';
import Button from '../components/ui/Button';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import Skeleton from '../components/ui/Skeleton';
import EventBus from '../utils/EventBus';
import styles from './Settings.module.css';

class Settings extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      me: null,
      passwordModalOpen: false,
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
      changingPassword: false,

      admins: [],
      adminsLoading: true,
      inviteOpen: false,
      inviteEmail: '',
      inviteName: '',
      inviting: false,
      invitedResult: null, // { admin, temporary_password } — shown once
      confirmRemoveId: null,
      removing: false,

      channelStatus: null,
      channelLoading: true,
    };
  }

  componentDidMount() {
    authService.me().then((me) => this.setState({ me })).catch(() => {});
    this.loadAdmins();
    settingsService
      .getChannelStatus()
      .then((channelStatus) => this.setState({ channelLoading: false, channelStatus }))
      .catch(() => this.setState({ channelLoading: false }));
  }

  loadAdmins() {
    this.setState({ adminsLoading: true });
    adminUserService
      .list()
      .then((admins) => this.setState({ adminsLoading: false, admins }))
      .catch((err) => {
        this.setState({ adminsLoading: false });
        EventBus.emit('toast', { type: 'error', message: err.message || 'Failed to load admins.' });
      });
  }

  changePassword() {
    const { currentPassword, newPassword, confirmPassword } = this.state;
    if (newPassword !== confirmPassword) {
      EventBus.emit('toast', { type: 'error', message: "New password and confirmation don't match." });
      return;
    }
    this.setState({ changingPassword: true });
    authService
      .changePassword({ currentPassword, newPassword })
      .then(() => {
        this.closePasswordModal();
        EventBus.emit('toast', { type: 'success', message: 'Password updated.' });
      })
      .catch((err) => {
        this.setState({ changingPassword: false });
        EventBus.emit('toast', { type: 'error', message: err.message || 'Failed to change password.' });
      });
  }

  closePasswordModal() {
    this.setState({
      passwordModalOpen: false,
      changingPassword: false,
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
  }

  inviteAdmin() {
    const { inviteEmail, inviteName } = this.state;
    this.setState({ inviting: true });
    adminUserService
      .create({ email: inviteEmail, name: inviteName })
      .then((result) => {
        this.setState({ inviting: false, invitedResult: result, inviteEmail: '', inviteName: '' });
        this.loadAdmins();
      })
      .catch((err) => {
        this.setState({ inviting: false });
        EventBus.emit('toast', { type: 'error', message: err.message || 'Failed to invite admin.' });
      });
  }

  closeInvite() {
    this.setState({ inviteOpen: false, invitedResult: null, inviteEmail: '', inviteName: '' });
  }

  removeAdmin() {
    const { confirmRemoveId } = this.state;
    this.setState({ removing: true });
    adminUserService
      .remove(confirmRemoveId)
      .then(() => {
        this.setState({ removing: false, confirmRemoveId: null });
        EventBus.emit('toast', { type: 'success', message: 'Admin deactivated.' });
        this.loadAdmins();
      })
      .catch((err) => {
        this.setState({ removing: false });
        EventBus.emit('toast', { type: 'error', message: err.message || 'Failed to deactivate admin.' });
      });
  }

  render() {
    const {
      me, passwordModalOpen, currentPassword, newPassword, confirmPassword, changingPassword,
      admins, adminsLoading, inviteOpen, inviteEmail, inviteName, inviting, invitedResult,
      confirmRemoveId, removing, channelStatus, channelLoading,
    } = this.state;

    const removeTarget = admins.find((a) => a.id === confirmRemoveId);

    return (
      <>
        <div className={styles.pageHead}>
          <div className={styles.pageTitle}>Settings</div>
        </div>

        <div className={styles.card}>
          <div className={styles.cardHead}>
            <div className={styles.cardTitle}>My profile</div>
            <Button variant="secondary" onClick={() => this.setState({ passwordModalOpen: true })}>Change password</Button>
          </div>
          <div className={styles.profileBody}>
            {!me ? (
              <Skeleton variant="text" width={240} height={18} />
            ) : (
              <div className={styles.profileInfo}>
                <div><span className={styles.profileLabel}>Email</span> {me.email}</div>
                <div><span className={styles.profileLabel}>Name</span> {me.name || '—'}</div>
              </div>
            )}
          </div>
        </div>

        <div className={styles.card}>
          <div className={styles.cardHead}>
            <div className={styles.cardTitle}>Admin users</div>
            <Button variant="secondary" onClick={() => this.setState({ inviteOpen: true })}>+ Invite admin</Button>
          </div>
          <div className={styles.adminTable}>
            <div className={styles.adminHeadRow}>
              <div className={styles.adminColEmail}>Email</div>
              <div className={styles.adminColName}>Name</div>
              <div className={styles.adminColDate}>Joined</div>
              <div className={styles.adminColActions} />
            </div>
            {adminsLoading ? (
              <div style={{ padding: 16 }}><Skeleton variant="text" width="100%" height={16} /></div>
            ) : (
              admins.map((a) => (
                <div key={a.id} className={styles.adminRow}>
                  <div className={styles.adminColEmail}>{a.email}</div>
                  <div className={styles.adminColName}>{a.name || '—'}</div>
                  <div className={styles.adminColDate}>{new Date(a.created_on).toLocaleDateString()}</div>
                  <div className={styles.adminColActions}>
                    {me && a.id !== me.id && (
                      <button className={styles.deactivateLink} onClick={() => this.setState({ confirmRemoveId: a.id })}>
                        Deactivate
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className={styles.card}>
          <div className={styles.cardHead}>
            <div className={styles.cardTitle}>Channel configuration</div>
            <div className={styles.cardSub}>Live status from the backend's own env config</div>
          </div>
          {channelLoading || !channelStatus ? (
            <div style={{ padding: 16 }}><Skeleton variant="text" width="100%" height={16} /></div>
          ) : (
            <>
              <div className={styles.channelList}>
                {channelStatus.channels.map((c) => (
                  <div key={c.key} className={styles.channelRow}>
                    <span className={[styles.channelDot, c.configured ? styles.channelOn : styles.channelOff].join(' ')} />
                    <span className={styles.channelName}>{c.name}</span>
                    <span className={c.configured ? styles.channelStatusOn : styles.channelStatusOff}>
                      {c.configured ? 'Configured' : 'Not configured'}
                    </span>
                    {c.webhook_url && <span className={styles.channelWebhook}>{c.webhook_url}</span>}
                  </div>
                ))}
              </div>
              <div className={styles.apiKeyRow}>
                <span className={styles.profileLabel}>Events API key</span>
                <code className={styles.apiKeyValue}>{channelStatus.events_api_key || 'not set'}</code>
              </div>
            </>
          )}
        </div>

        <Modal open={passwordModalOpen} onClose={() => this.closePasswordModal()} title="Change password" width="400px">
          <div>
            <div className={styles.formRow}>
              <label className={styles.formLabel}>Current password</label>
              <input
                type="password"
                className={styles.input}
                value={currentPassword}
                onChange={(e) => this.setState({ currentPassword: e.target.value })}
              />
            </div>
            <div className={styles.formRow}>
              <label className={styles.formLabel}>New password</label>
              <input
                type="password"
                className={styles.input}
                value={newPassword}
                onChange={(e) => this.setState({ newPassword: e.target.value })}
              />
            </div>
            <div className={styles.formRow}>
              <label className={styles.formLabel}>Confirm new password</label>
              <input
                type="password"
                className={styles.input}
                value={confirmPassword}
                onChange={(e) => this.setState({ confirmPassword: e.target.value })}
              />
            </div>
            <Button
              variant="primary"
              loading={changingPassword}
              disabled={!currentPassword || !newPassword || !confirmPassword}
              onClick={() => this.changePassword()}
            >
              Update password
            </Button>
          </div>
        </Modal>

        <Modal open={inviteOpen} onClose={() => this.closeInvite()} title="Invite admin" width="440px">
          {invitedResult ? (
            <div>
              <div className={styles.tempPasswordCallout}>
                <div className={styles.tempPasswordLabel}>Temporary password — copy this now, it won't be shown again</div>
                <code className={styles.tempPasswordValue}>{invitedResult.temporary_password}</code>
              </div>
              <div className={styles.formRow} style={{ marginTop: 14 }}>
                <Button variant="primary" onClick={() => this.closeInvite()}>Done</Button>
              </div>
            </div>
          ) : (
            <div>
              <div className={styles.formRow}>
                <label className={styles.formLabel}>Email</label>
                <input
                  className={styles.input}
                  value={inviteEmail}
                  onChange={(e) => this.setState({ inviteEmail: e.target.value })}
                />
              </div>
              <div className={styles.formRow}>
                <label className={styles.formLabel}>Name (optional)</label>
                <input
                  className={styles.input}
                  value={inviteName}
                  onChange={(e) => this.setState({ inviteName: e.target.value })}
                />
              </div>
              <Button variant="primary" loading={inviting} disabled={!inviteEmail} onClick={() => this.inviteAdmin()}>
                Create admin
              </Button>
            </div>
          )}
        </Modal>

        <ConfirmDialog
          open={!!confirmRemoveId}
          title="Deactivate admin?"
          message={removeTarget ? `${removeTarget.email} will no longer be able to log in.` : ''}
          confirmLabel="Deactivate"
          loading={removing}
          onConfirm={() => this.removeAdmin()}
          onCancel={() => this.setState({ confirmRemoveId: null })}
        />
      </>
    );
  }
}

export default Settings;
